/**
 * WebSocket client for real-time PMS updates
 */

class PMSWebSocketClient {
  constructor() {
    this.ws = null;
    this.propertyId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.eventHandlers = new Map();
    this.isConnected = false;
  }

  connect(propertyId, token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[WS] Already connected');
      return;
    }

    this.propertyId = propertyId;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/properties/${propertyId}?token=${token}`;

    console.log('[WS] Connecting to:', wsUrl);

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('[WS] Connected successfully');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.notifyHandlers('connection', { connected: true });
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[WS] Received event:', data.type);

        if (data.type === 'ping') {
          this.ws.send(JSON.stringify({ type: 'pong' }));
          return;
        }

        if (data.type === 'connected') {
          console.log('[WS] Connection confirmed');
          return;
        }

        // Dispatch to handlers
        this.notifyHandlers(data.type, data);
      } catch (error) {
        console.error('[WS] Error parsing message:', error);
      }
    };

    this.ws.onclose = () => {
      console.log('[WS] Connection closed');
      this.isConnected = false;
      this.notifyHandlers('connection', { connected: false });
      this.attemptReconnect(propertyId, token);
    };

    this.ws.onerror = (error) => {
      console.error('[WS] WebSocket error:', error);
    };
  }

  attemptReconnect(propertyId, token) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WS] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect(propertyId, token);
    }, delay);
  }

  disconnect() {
    if (this.ws) {
      console.log('[WS] Disconnecting...');
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }

  on(eventType, handler) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType).push(handler);
  }

  off(eventType, handler) {
    if (this.eventHandlers.has(eventType)) {
      const handlers = this.eventHandlers.get(eventType);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  notifyHandlers(eventType, data) {
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[WS] Error in ${eventType} handler:`, error);
      }
    });
  }
}

// Global singleton instance
export const wsClient = new PMSWebSocketClient();

export default wsClient;
